import { faEllipsisH, faXmark } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { keyframes, styled, css } from "styled-components";
import { transition } from "../../styles/mixins";
import { useEffect, useRef, useState } from "react";
import ShipDetail from './ShipDetail';
import ShipType from "./ShipType";
import Typography from "../common/Typography";
import Window from "../common/Window";

const transitAnimation = keyframes`
  from {
    width: 0;
  }

  to {
    width: 100%;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Ship = styled.div`
  position: relative;
`;

const ShipMore = styled(Ship)`
  margin-top: auto;
`;

const ShipTile = styled.div`
  ${transition('background-color')}
  align-items: center;
  aspect-ratio: 1;
  background-color: ${props => props.$selected  ? 'var(--color-theme-accent-500)' : 'transparent'};
  border-bottom: var(--border-width) var(--border-style) var(--border-color);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  row-gap: var(--space-2);
`;

const ShipWindow = styled.div`
  ${transition('opacity, scale')}
  opacity: ${props => props.$isOpen ? '1' : '0'};
  pointer-events: ${props => props.$isOpen ? 'all' : 'none'};
  position: absolute;
  right: -12px;
  scale: ${props => props.$isOpen ? '1' : '0.5'};
  translate: 100% 0;
  width: 288px;

  ${(props) => !props.$position && css`
    top: 0;
    transform-origin: top left;
  `}

  ${(props) => props.$position && css`
    bottom: 0;
    transform-origin: bottom left;
  `}

  &::before {
    aspect-ratio: 1;
    background-color: var(--color-theme-primary-400);
    border-bottom: var(--border-width) var(--border-style) var(--window-border);
    border-left: var(--border-width) var(--border-style) var(--window-border);
    content: '';
    left: 0;
    position: absolute;
    rotate: 45deg;
    translate: -50% -50%;
    width: var(--space-3);

    ${(props) => !props.$position && css`
      top: calc(72px / 2);
      translate: -50% -50%;
    `}

    ${(props) => props.$position && css`
      bottom: 35px;
      translate: -50% 50%;
    `}
  }
`;

const ShipWindowClose = styled(Window)`
  align-items: center;
  aspect-ratio: 1;
  display: flex;
  justify-content: center;
  position: absolute;
  right: 0;
  top: 0;
  width: var(--space-6);
`;

const Transit = styled.div`
  height: 2px;
  position: absolute;
  top: 0;
  width: 100%;

  &::after {
    animation-duration: ${props => props.$duration }s;
    animation-fill-mode: forwards;
    animation-name: ${transitAnimation};
    animation-timing-function: linear;
    background-color: var(--color-theme-accent-400);
    content: '';
    height: inherit;
    left: 0;
    position: absolute;
    top: 0;
    width: 0;
  }
`;

function FleetOverview({ store }) {
  let shipElements = [];
  const ref = useRef();
  const fleet = store.state.ships;
  const selectedShip = store.state.selectedShip;
  const [isOpen, setOpen] = useState(false);
  const [windowPosition, setWindowPosition] = useState(false);
  const [shipIdx, setShipIdx] = useState(0);
  const [shipAmount, setShipAmount] = useState(0);

  const clickHandle = (symbol, idx) => {
    setOpen(true);
    setShipIdx(idx);
    store.selectShip(symbol);
  }

  const closeHandle = () => {
    if (isOpen) {
      setOpen(false);
    }
  }

  useEffect(() => {
    const list = ref.current;
    const ship = shipElements[shipIdx];
    const shipOffsetTop = ship && ship.getBoundingClientRect().top;
    const shipWindowHeight = ship && ship.querySelector('.window').offsetHeight;
    const shipOffsetBottom = shipOffsetTop + shipWindowHeight;
    const treshold = list.offsetHeight + 40 + 48;

    if (shipOffsetBottom >= treshold) {
      setWindowPosition(true);
    } else {
      setWindowPosition(false);
    }

  }, [shipIdx]);

  useEffect(() => {
    setShipAmount(Math.floor(ref.current.offsetHeight / 72) - 1);
  });

  return (
    <List ref={ref}>
      {fleet && fleet.slice(0,shipAmount).map((ship, idx) => (
        <Ship ref={ref => shipElements[idx] = ref} key={idx}>
          <ShipTile $selected={ship.symbol === selectedShip} onClick={() => clickHandle(ship.symbol, idx)}>
            {ship.action && <Transit $duration={ ship.action.duration }/>}
            <ShipType ship={ship} size="xl" />
            <Typography fontSize="xs">{ship.symbol}</Typography>
          </ShipTile>
          <ShipWindow className="window" $position={windowPosition} $isOpen={ship.symbol === selectedShip && isOpen}>
            <Window $padding>
              <ShipDetail store={store} />
            </Window>
            <ShipWindowClose onClick={closeHandle}>
              <FontAwesomeIcon icon={faXmark} />
            </ShipWindowClose>
          </ShipWindow>
        </Ship>
      ))}
      {fleet && fleet.length > shipAmount &&
        <ShipMore onClick={() => store.setModal('fleet')}>
          <ShipTile>
            <FontAwesomeIcon icon={faEllipsisH} size="lg" />
          </ShipTile>
        </ShipMore>
      }
    </List>
  )
}

export default FleetOverview;
